import { Contest, Nullable } from '@Models';
import { RequestAction } from '@Repository/RequestAction';
import { RequestArmyListSuccess } from '@Repository/builder';
import {
  MyContestRepository,
  RequestContestSuccess,
} from '@Repository/tournament';
import { Locale } from '@Repository/locale';
import { inject, injectable } from 'inversify';
import { Result } from '@Redux/type';
import { TYPES } from '@Types';
import { DeleteRepository } from '@Repository/DeleteRepository';
import { PayloadAction } from '@reduxjs/toolkit';
import { IEditableEntityAction } from '@Redux/IEditableEntityAction';
import myContestSlice from '@Redux/reducers/tournament/myContest.slice';
import { ContestParams } from '@Builder/myBuilder/tournament/new/contest/validate';

@injectable()
export class MyContestAction extends IEditableEntityAction<
  string,
  Contest,
  RequestContestSuccess
> {
  protected fetchDataSuccess(item: Contest): PayloadAction<Contest> {
    return myContestSlice.actions.setContest(item);
  }

  protected cacheDataSuccess(item: Contest): PayloadAction<Contest> {
    return myContestSlice.actions.setContest(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return myContestSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return myContestSlice.actions.setLoading(bool);
  }

  @inject(TYPES.Locale) private _locale!: Locale;
  @inject(TYPES.Repository.MyContestRepository)
  private _repository!: MyContestRepository;

  protected getCachedItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<Nullable<Contest>> {
    return this._repository.getCached(entityId, { locale: locale });
  }

  protected getItem({
    entityId,
    locale,
  }: {
    entityId: string;
    locale: Nullable<string>;
  }): Promise<RequestAction> {
    return this._repository.get({ locale: locale, id: entityId });
  }

  public create =
    (contestValues: ContestParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const contest = {
        ...contestValues,
      } satisfies Contest as Contest;

      const response = await this._repository.create({
        contest: contest,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestArmyListSuccess;
      window.location.href = `/${this._locale.getlocale()}/tournament/contests/${
        success.payload.id
      }`;
    };

  public edit =
    (contest: Contest): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const response = await this._repository.update({
        id: contest.id,
        contest: contest,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestContestSuccess;
      dispatch(this.fetchDataSuccess(success.payload));
      dispatch(this.areLoading(false));
      dispatch(myContestSlice.actions.cancel());
    };

  getDeleteRepository(): DeleteRepository<string> {
    return this._repository;
  }
}
